import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Target, Plus, Edit, Calendar as CalendarIcon, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSRocks = () => {
  const [rocks, setRocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRock, setSelectedRock] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1 2025');
  const [filterType, setFilterType] = useState('all'); // all, company, department, personal

  // Form state
  const [rockForm, setRockForm] = useState({
    title: '',
    description: '',
    success_criteria: '',
    owner_id: '',
    rock_type: 'company',
    department: '',
    quarter: 'Q1 2025',
    quarter_start_date: '',
    quarter_end_date: ''
  });

  // Update progress form
  const [progressForm, setProgressForm] = useState({
    progress_percentage: 0,
    status: '',
    notes: ''
  });

  useEffect(() => {
    fetchRocks();
    fetchUsers();
    setDefaultQuarterDates();
  }, []);

  useEffect(() => {
    fetchRocks();
  }, [selectedQuarter, filterType]);

  const fetchRocks = async () => {
    try {
      const params = {};
      if (selectedQuarter !== 'all') params.quarter = selectedQuarter;
      if (filterType !== 'all') params.rock_type = filterType;

      const response = await axios.get(`${API}/eos/rocks`, { params });
      setRocks(response.data);
    } catch (error) {
      console.error('Error fetching rocks:', error);
      toast.error('Failed to load rocks');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const setDefaultQuarterDates = () => {
    // Q1 2025: Jan 1 - Mar 31
    const startDate = new Date('2025-01-01');
    const endDate = new Date('2025-03-31');
    
    setRockForm(prev => ({
      ...prev,
      quarter_start_date: startDate.toISOString().split('T')[0],
      quarter_end_date: endDate.toISOString().split('T')[0]
    }));
  };

  const handleCreateRock = async () => {
    try {
      // Validate
      if (!rockForm.title || !rockForm.owner_id || !rockForm.success_criteria) {
        toast.error('Please fill in all required fields');
        return;
      }

      const payload = {
        ...rockForm,
        quarter_start_date: new Date(rockForm.quarter_start_date).toISOString(),
        quarter_end_date: new Date(rockForm.quarter_end_date).toISOString()
      };

      await axios.post(`${API}/eos/rocks`, payload);
      toast.success('Rock created successfully');
      setCreateDialogOpen(false);
      resetRockForm();
      fetchRocks();
    } catch (error) {
      console.error('Error creating rock:', error);
      toast.error(error.response?.data?.detail || 'Failed to create rock');
    }
  };

  const handleUpdateProgress = async () => {
    try {
      if (!selectedRock) return;

      const payload = {
        progress_percentage: parseInt(progressForm.progress_percentage),
        status: progressForm.status || undefined,
        notes: progressForm.notes
      };

      await axios.put(`${API}/eos/rocks/${selectedRock.id}/progress`, payload);
      toast.success('Rock progress updated successfully');
      setUpdateDialogOpen(false);
      setSelectedRock(null);
      setProgressForm({ progress_percentage: 0, status: '', notes: '' });
      fetchRocks();
    } catch (error) {
      console.error('Error updating rock progress:', error);
      toast.error('Failed to update rock progress');
    }
  };

  const openUpdateDialog = (rock) => {
    setSelectedRock(rock);
    setProgressForm({
      progress_percentage: rock.progress_percentage,
      status: rock.status,
      notes: ''
    });
    setUpdateDialogOpen(true);
  };

  const openDetailDialog = (rock) => {
    setSelectedRock(rock);
    setDetailDialogOpen(true);
  };

  const resetRockForm = () => {
    setRockForm({
      title: '',
      description: '',
      success_criteria: '',
      owner_id: '',
      rock_type: 'company',
      department: '',
      quarter: 'Q1 2025',
      quarter_start_date: '',
      quarter_end_date: ''
    });
    setDefaultQuarterDates();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'bg-blue-500';
      case 'on_track': return 'bg-green-500';
      case 'at_risk': return 'bg-yellow-500';
      case 'off_track': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'complete': return '🎉 Complete';
      case 'on_track': return '🟢 On Track';
      case 'at_risk': return '🟡 At Risk';
      case 'off_track': return '🔴 Off Track';
      default: return '⚪ Not Started';
    }
  };

  const getRockTypeIcon = (type) => {
    switch (type) {
      case 'company': return '🏢';
      case 'department': return '🏬';
      case 'personal': return '👤';
      default: return '📌';
    }
  };

  const calculateDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diff = end - today;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const calculateOverallStats = () => {
    const total = rocks.length;
    const complete = rocks.filter(r => r.is_complete).length;
    const onTrack = rocks.filter(r => r.status === 'on_track' && !r.is_complete).length;
    const atRisk = rocks.filter(r => r.status === 'at_risk').length;
    const offTrack = rocks.filter(r => r.status === 'off_track').length;
    const avgProgress = total > 0 
      ? Math.round(rocks.reduce((sum, r) => sum + r.progress_percentage, 0) / total)
      : 0;

    return { total, complete, onTrack, atRisk, offTrack, avgProgress };
  };

  const stats = calculateOverallStats();

  // Group rocks by type
  const companyRocks = rocks.filter(r => r.rock_type === 'company');
  const departmentRocks = rocks.filter(r => r.rock_type === 'department');
  const personalRocks = rocks.filter(r => r.rock_type === 'personal');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Rocks...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="h-8 w-8 text-purple-600" />
            <div>
              <h1 className="text-3xl font-bold">Rocks: {selectedQuarter}</h1>
              <p className="text-sm text-muted-foreground">
                90-day priorities and quarterly goals
              </p>
            </div>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Rock
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Q1 2025">Q1 2025</SelectItem>
              <SelectItem value="Q2 2025">Q2 2025</SelectItem>
              <SelectItem value="Q3 2025">Q3 2025</SelectItem>
              <SelectItem value="Q4 2025">Q4 2025</SelectItem>
              <SelectItem value="all">All Quarters</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="company">Company</SelectItem>
              <SelectItem value="department">Department</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Overall Progress */}
        <Card>
          <CardHeader>
            <CardTitle>Overall Progress</CardTitle>
            <CardDescription>{selectedQuarter} - {calculateDaysRemaining(rockForm.quarter_end_date || '2025-03-31')} days remaining</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total Rocks</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.complete}</div>
                <div className="text-sm text-muted-foreground">✅ Complete</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{stats.onTrack}</div>
                <div className="text-sm text-muted-foreground">🟢 On Track</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{stats.atRisk}</div>
                <div className="text-sm text-muted-foreground">🟡 At Risk</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{stats.offTrack}</div>
                <div className="text-sm text-muted-foreground">🔴 Off Track</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">{stats.avgProgress}%</div>
                <div className="text-sm text-muted-foreground">Avg Progress</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                  style={{ width: `${stats.avgProgress}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Rocks */}
        {(filterType === 'all' || filterType === 'company') && companyRocks.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏢</span>
                <div>
                  <CardTitle>Company Rocks</CardTitle>
                  <CardDescription>{companyRocks.length} company-wide priorities</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {companyRocks.map(rock => (
                  <Card key={rock.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetailDialog(rock)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{rock.title}</h3>
                            <Badge variant={rock.is_complete ? 'default' : 'outline'}>
                              {getStatusLabel(rock.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            Owner: {rock.owner_details 
                              ? `${rock.owner_details.first_name} ${rock.owner_details.last_name}`
                              : 'N/A'}
                          </p>
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-1">
                                <span>{rock.progress_percentage}% Complete</span>
                                <span className="text-muted-foreground">
                                  {calculateDaysRemaining(rock.quarter_end_date)} days left
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getStatusColor(rock.status)}`}
                                  style={{ width: `${rock.progress_percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                          {rock.success_criteria && (
                            <p className="text-xs text-muted-foreground italic">
                              Success: {rock.success_criteria}
                            </p>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openUpdateDialog(rock);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Update
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Department Rocks */}
        {(filterType === 'all' || filterType === 'department') && departmentRocks.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏬</span>
                <div>
                  <CardTitle>Department Rocks</CardTitle>
                  <CardDescription>{departmentRocks.length} department-specific goals</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {departmentRocks.map(rock => (
                  <Card key={rock.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetailDialog(rock)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{rock.title}</h3>
                            <Badge variant="outline">{rock.department}</Badge>
                            <Badge variant={rock.is_complete ? 'default' : 'outline'}>
                              {getStatusLabel(rock.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            Owner: {rock.owner_details 
                              ? `${rock.owner_details.first_name} ${rock.owner_details.last_name}`
                              : 'N/A'}
                          </p>
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-1">
                                <span>{rock.progress_percentage}% Complete</span>
                                <span className="text-muted-foreground">
                                  {calculateDaysRemaining(rock.quarter_end_date)} days left
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getStatusColor(rock.status)}`}
                                  style={{ width: `${rock.progress_percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openUpdateDialog(rock);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Update
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Rocks */}
        {(filterType === 'all' || filterType === 'personal') && personalRocks.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className="text-2xl">👤</span>
                <div>
                  <CardTitle>Personal Rocks</CardTitle>
                  <CardDescription>{personalRocks.length} individual goals</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {personalRocks.map(rock => (
                  <Card key={rock.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetailDialog(rock)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{rock.title}</h3>
                            <Badge variant={rock.is_complete ? 'default' : 'outline'}>
                              {getStatusLabel(rock.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            Owner: {rock.owner_details 
                              ? `${rock.owner_details.first_name} ${rock.owner_details.last_name}`
                              : 'N/A'}
                          </p>
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex-1">
                              <div className="flex justify-between text-sm mb-1">
                                <span>{rock.progress_percentage}% Complete</span>
                                <span className="text-muted-foreground">
                                  {calculateDaysRemaining(rock.quarter_end_date)} days left
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getStatusColor(rock.status)}`}
                                  style={{ width: `${rock.progress_percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openUpdateDialog(rock);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Update
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {rocks.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No rocks for {selectedQuarter}. Create your first rock to get started!</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Rock
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Rock Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Rock</DialogTitle>
              <DialogDescription>Define a 90-day priority or goal</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Rock Title *</Label>
                <Input
                  placeholder="e.g., Grow revenue by 40%"
                  value={rockForm.title}
                  onChange={(e) => setRockForm({ ...rockForm, title: e.target.value })}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of what this rock entails"
                  value={rockForm.description}
                  onChange={(e) => setRockForm({ ...rockForm, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label>Success Criteria *</Label>
                <Input
                  placeholder="e.g., Hit $700K revenue by March 31, 2025"
                  value={rockForm.success_criteria}
                  onChange={(e) => setRockForm({ ...rockForm, success_criteria: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  How will you know when this rock is complete?
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Owner *</Label>
                  <Select 
                    value={rockForm.owner_id}
                    onValueChange={(value) => setRockForm({ ...rockForm, owner_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner">
                        {rockForm.owner_id && users.find(u => u.id === rockForm.owner_id || u.user_id === rockForm.owner_id)
                          ? `${users.find(u => u.id === rockForm.owner_id || u.user_id === rockForm.owner_id).first_name} ${users.find(u => u.id === rockForm.owner_id || u.user_id === rockForm.owner_id).last_name}`
                          : "Select owner"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id || user.user_id} value={user.id || user.user_id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Rock Type *</Label>
                  <Select 
                    value={rockForm.rock_type}
                    onValueChange={(value) => setRockForm({ ...rockForm, rock_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">🏢 Company Rock</SelectItem>
                      <SelectItem value="department">🏬 Department Rock</SelectItem>
                      <SelectItem value="personal">👤 Personal Rock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {rockForm.rock_type === 'department' && (
                <div>
                  <Label>Department *</Label>
                  <Select 
                    value={rockForm.department}
                    onValueChange={(value) => setRockForm({ ...rockForm, department: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Quarter *</Label>
                <Select 
                  value={rockForm.quarter}
                  onValueChange={(value) => {
                    setRockForm({ ...rockForm, quarter: value });
                    // Auto-set dates based on quarter
                    if (value === 'Q1 2025') {
                      setRockForm(prev => ({
                        ...prev,
                        quarter: value,
                        quarter_start_date: '2025-01-01',
                        quarter_end_date: '2025-03-31'
                      }));
                    } else if (value === 'Q2 2025') {
                      setRockForm(prev => ({
                        ...prev,
                        quarter: value,
                        quarter_start_date: '2025-04-01',
                        quarter_end_date: '2025-06-30'
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Q1 2025">Q1 2025 (Jan-Mar)</SelectItem>
                    <SelectItem value="Q2 2025">Q2 2025 (Apr-Jun)</SelectItem>
                    <SelectItem value="Q3 2025">Q3 2025 (Jul-Sep)</SelectItem>
                    <SelectItem value="Q4 2025">Q4 2025 (Oct-Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={rockForm.quarter_start_date}
                    onChange={(e) => setRockForm({ ...rockForm, quarter_start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={rockForm.quarter_end_date}
                    onChange={(e) => setRockForm({ ...rockForm, quarter_end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetRockForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateRock}>Create Rock</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Progress Dialog */}
        <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Rock Progress</DialogTitle>
              <DialogDescription>{selectedRock?.title}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Progress Percentage (0-100) *</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={progressForm.progress_percentage}
                  onChange={(e) => setProgressForm({ ...progressForm, progress_percentage: e.target.value })}
                />
                <div className="mt-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500"
                      style={{ width: `${progressForm.progress_percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={progressForm.status}
                  onValueChange={(value) => setProgressForm({ ...progressForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auto-calculated or manual override" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">⚪ Not Started</SelectItem>
                    <SelectItem value="on_track">🟢 On Track</SelectItem>
                    <SelectItem value="at_risk">🟡 At Risk</SelectItem>
                    <SelectItem value="off_track">🔴 Off Track</SelectItem>
                    <SelectItem value="complete">🎉 Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Update Notes</Label>
                <Textarea
                  placeholder="What progress was made? Any blockers?"
                  value={progressForm.notes}
                  onChange={(e) => setProgressForm({ ...progressForm, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setUpdateDialogOpen(false);
                setSelectedRock(null);
                setProgressForm({ progress_percentage: 0, status: '', notes: '' });
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateProgress}>Update Progress</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rock Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedRock?.title}</DialogTitle>
              <DialogDescription>
                {getRockTypeIcon(selectedRock?.rock_type)} {selectedRock?.rock_type} Rock
              </DialogDescription>
            </DialogHeader>

            {selectedRock && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Description</Label>
                  <p>{selectedRock.description || 'No description provided'}</p>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Success Criteria</Label>
                  <p className="font-medium">{selectedRock.success_criteria}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Owner</Label>
                    <p>
                      {selectedRock.owner_details 
                        ? `${selectedRock.owner_details.first_name} ${selectedRock.owner_details.last_name}`
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Quarter</Label>
                    <p>{selectedRock.quarter}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Progress</Label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getStatusColor(selectedRock.status)}`}
                          style={{ width: `${selectedRock.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="font-semibold">{selectedRock.progress_percentage}%</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Status</Label>
                  <p className="text-lg font-semibold">{getStatusLabel(selectedRock.status)}</p>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Timeline</Label>
                  <p>
                    {new Date(selectedRock.quarter_start_date).toLocaleDateString()} - {new Date(selectedRock.quarter_end_date).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {calculateDaysRemaining(selectedRock.quarter_end_date)} days remaining
                  </p>
                </div>

                {selectedRock.update_history && selectedRock.update_history.length > 0 && (
                  <div>
                    <Label className="text-sm text-muted-foreground">Recent Updates</Label>
                    <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                      {selectedRock.update_history.slice(-5).reverse().map((update, index) => (
                        <div key={index} className="text-sm p-2 bg-muted rounded">
                          <div className="flex justify-between">
                            <span className="font-medium">{update.progress_before}% → {update.progress_after}%</span>
                            <span className="text-muted-foreground">
                              {new Date(update.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                          {update.notes && <p className="text-muted-foreground mt-1">{update.notes}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                openUpdateDialog(selectedRock);
                setDetailDialogOpen(false);
              }}>
                <Edit className="h-4 w-4 mr-2" />
                Update Progress
              </Button>
              <Button onClick={() => {
                setDetailDialogOpen(false);
                setSelectedRock(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSRocks;
